import { fmtDisplayDate } from '@utils/displayDate'
import { readSavedExagoRun, savedExagoKind } from '@pages/reports/exagoAgedArJob'
import { exagoFilterParts } from '@pages/reports/exagoAgedArGrid'
import {
  AS_OF_LABEL,
  FILTER_LOGIC_NOTE,
  START_DATE_LABEL,
  datePart,
  derivedParts,
  ranDateParts,
  savedFilterText,
  usesTodaySentence,
} from '@pages/reports/exagoPromptFields'

/**
 * Spec 215 — the queue drawer's "Filters used" for an Advanced (Exago) run.
 *
 * An Exago job's stored params are the run route's query (`exagoQueueEndpoint`): `asOf` (a
 * date, or `@today` for a schedule that rolls with each run), `earliest` (a generic run sends
 * it only when the user chose a start date; a reviewed run, spec 190, sends it every time,
 * 1900-01-01 by default), and `tz` only beside `@today`. The drawer printed those keys at the
 * user ("asOf: 11/02/2026", "tz: America/Chicago") and none of the report's saved filters,
 * while the page says "Date range: 01/01/2022 – 11/02/2026" and lists the saved filters as
 * chips (spec 214 review critic C3).
 *
 * So the drawer reads what the page reads:
 *
 *   - **A finished run whose stored result reads** states exactly what that run says it ran
 *     with, through the page's own producers, in the page's order. A generic run (spec 209):
 *     its dates in its own labels (`ranDateParts`) and the CB-set parameters it used
 *     (`derivedParts`), then `usesToday`'s sentence when it holds, which is about THOSE dates;
 *     then, as their own group, each saved value as its chip reads (`savedFilterText`) and the
 *     filter-logic note when how they combine is unread. A reviewed run (spec 190): the parts
 *     its documents state (`exagoFilterParts`).
 *   - **Any other run** (queued, running, failed, cancelled, a result that is gone, one this
 *     reader may not open, or one that does not read) has only the dates it was SENT. They are
 *     stated in the generic page's default labels, "As of" and "Start date", with `@today` as
 *     "the day of the run", and said to be what was sent: the drawer cannot tell which of them
 *     the report uses (a report with no date control is still sent an as-of date), nor its
 *     saved filters. The zone is never a filter.
 *
 * Nothing else is ever printed: an unknown key, or a value that is not a real day in the wire's
 * shape, says nothing, so no raw key or value reaches the user. The stored params are untouched.
 */

/** The fallback's note when it states sent dates (spec 215). */
export const SENT_DATES_NOTE = 'These are the dates this run was sent. Report Center can’t show how the report uses them, or any saved filters it has.'

/** The fallback's note when not even a date could be stated (spec 215). */
export const SAVED_FILTERS_UNSHOWN = 'Report Center can’t show this run’s saved filters, if it has any.'

/** `@today`, as the as-of date of a run that rolls with each run (`exagoQueueEndpoint`). */
export const RUN_DAY_PHRASE = 'the day of the run'

const AS_OF_TODAY = '@today'
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const NONE = Object.freeze({ chips: [], notes: [], saved: null })

/**
 * A stored result body → `{ jobId, kind, data }`, or null when it does not read. Never throws:
 * a body the page itself would refuse states nothing here either.
 */
export function readExagoRunForDrawer(jobId, body) {
  try {
    return { jobId, kind: savedExagoKind(body), data: readSavedExagoRun(body) }
  } catch {
    return null
  }
}

const listOf = (v) => (Array.isArray(v) ? v : [])

const genericRun = (data) => {
  const p = data.parameters
  const dates = ranDateParts(p)
  const fixed = listOf(p.fixed)
  // As the page decides it with no /info: the run's own flag, else in doubt whenever one of its
  // saved values is an Exago filter (`source: 'filter'`, not a prompt parameter or dropdown) —
  // ExagoGenericReport.jsx, review finding 16 of spec 214.
  const logicUnread = typeof data.notes?.filterLogicUnread === 'boolean'
    ? data.notes.filterLogicUnread
    : fixed.some((f) => f?.source === 'filter')
  return {
    chips: [...dates, ...derivedParts(p.derived)],
    notes: data.notes?.usesToday === true ? [usesTodaySentence(dates.length > 0)] : [],
    saved: fixed.length > 0
      ? { chips: fixed.map(savedFilterText), notes: logicUnread ? [FILTER_LOGIC_NOTE] : [] }
      : null,
  }
}

const reviewedRun = (data) => ({
  chips: exagoFilterParts(data.parameters, listOf(data.parameters.fixed)),
  notes: [],
  saved: null,
})

/**
 * A stored date that is a real day on the calendar, in the wire's own shape. dayjs ROLLS an
 * impossible date forward (`fmtDisplayDate('2026-02-31')` is 03/03/2026, measured for
 * `savedFilterChips.js`), so the display is turned back into the wire's shape and compared: a
 * date the run never sent is never stated.
 */
const realDay = (v) => {
  const iso = String(v ?? '')
  if (!ISO_DATE.test(iso)) return false
  const out = fmtDisplayDate(iso)
  return !!out && `${out.slice(6)}-${out.slice(0, 2)}-${out.slice(3, 5)}` === iso
}

/** The dates a run was SENT, when its result cannot be read. */
const sentDates = (savedParams) => {
  const { asOf, earliest } = savedParams
  const asOfPart = asOf === AS_OF_TODAY
    ? `${AS_OF_LABEL}: ${RUN_DAY_PHRASE}`
    : (realDay(asOf) ? datePart(AS_OF_LABEL, asOf) : null)
  const startPart = realDay(earliest) ? datePart(START_DATE_LABEL, earliest) : null
  return [asOfPart, startPart].filter(Boolean)
}

/**
 * What an Exago job's "Filters used" states: `{ chips, notes, saved }`. `chips` and `notes` are
 * the run's own dates and their caveat; `saved` is null or `{ chips, notes }`, the report's saved
 * values and theirs. Every entry is a plain string, in the order it is said.
 *
 * @param {{ run?: { kind: string, data: object } | null, savedParams?: object | null, pending?: boolean }} args
 *   `run` is the job's stored result as `readExagoRunForDrawer` read it (for THIS job), or
 *   null; `pending` is true while that read is still in flight, when nothing is said yet so
 *   the drawer never shows one wording and then another.
 */
export function exagoFiltersUsed({ run = null, savedParams = null, pending = false } = {}) {
  if (run?.data) return run.kind === 'generic' ? genericRun(run.data) : reviewedRun(run.data)
  if (pending) return NONE
  if (!savedParams || typeof savedParams !== 'object' || Object.keys(savedParams).length === 0) return NONE
  const chips = sentDates(savedParams)
  return { chips, notes: [chips.length > 0 ? SENT_DATES_NOTE : SAVED_FILTERS_UNSHOWN], saved: null }
}
